import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { marketAPI, paperAPI } from '../services/api'
import type { PaperPosition, TickerQuote } from '../types'
import { formatPrice } from '../utils/marketPrice'
import { formatPnlPct } from '../utils/pnl'
import { PaperOpenCounts, loadPaperOpenCounts, plural } from '../utils/paperCounts'
import { displayBase } from '../utils/displaySymbol'
import TerminalCanvas from '../theme/TerminalCanvas'

// Matches the dashboard panel's poll so the two views never disagree for long.
export const POLL_MS = 60_000

// The ledger holds one sub-position per breakout window (up to four a coin), so
// the row count runs well past the coin count. 500 is the API's own ceiling.
export const POSITION_LIMIT = 500

export type SortKey = 'pnl' | 'stop' | 'coin'

const SORT_LABELS: Record<SortKey, string> = {
  pnl: 'P&L',
  stop: 'Distance to stop',
  coin: 'Coin',
}

/**
 * One open sub-position, joined to the live tape.
 *
 * Every derived number here is display arithmetic over what the engine already
 * stored. Nothing on this page recalculates the stop: `stopPrice` is the
 * engine's own ratcheted `stop_price`, straight from the API.
 */
export interface PaperPositionRow {
  position: PaperPosition
  symbol: string
  base: string
  window: number
  entry: number
  atr: number
  stopPrice: number
  /** Last traded price, or null when the tape has no quote for this coin. */
  price: number | null
  /** Unrealised move from entry, before fees. Null without a price. */
  pnlPct: number | null
  /** How far the price sits above the stop, as a percent of the stop. */
  aboveStopPct: number | null
  /**
   * The same cushion counted in ATR units. Measured against the ATR stored at
   * entry — the engine re-reads ATR(14) each day to ratchet the stop, so this
   * is a stable yardstick for "how far from being stopped out", not a
   * reproduction of the engine's own arithmetic.
   */
  aboveStopAtr: number | null
}

/** Join positions to the tape and pre-compute every displayed number. */
export const buildRows = (
  positions: PaperPosition[],
  prices: Record<string, number>
): PaperPositionRow[] =>
  positions.map((position) => {
    const price = prices[position.symbol] ?? null
    const stopPrice = position.stop_price
    const atr = position.atr_at_entry
    return {
      position,
      symbol: position.symbol,
      base: displayBase(position.symbol),
      window: position.window,
      entry: position.entry_price,
      atr,
      stopPrice,
      price,
      pnlPct:
        price != null && position.entry_price > 0
          ? ((price - position.entry_price) / position.entry_price) * 100
          : null,
      aboveStopPct: price != null && stopPrice > 0 ? ((price - stopPrice) / stopPrice) * 100 : null,
      aboveStopAtr: price != null && atr > 0 ? (price - stopPrice) / atr : null,
    }
  })

/**
 * Sort by the chosen key. A row the tape could not price has no P&L and no
 * cushion, so it sorts last rather than being treated as zero.
 */
export const sortRows = (rows: PaperPositionRow[], key: SortKey): PaperPositionRow[] => {
  const copy = [...rows]
  const byCoin = (a: PaperPositionRow, b: PaperPositionRow) =>
    a.base.localeCompare(b.base) || a.window - b.window

  if (key === 'coin') return copy.sort(byCoin)

  const value = (row: PaperPositionRow) => (key === 'pnl' ? row.pnlPct : row.aboveStopPct)
  return copy.sort((a, b) => {
    const left = value(a)
    const right = value(b)
    if (left == null && right == null) return byCoin(a, b)
    if (left == null) return 1
    if (right == null) return -1
    // Worst first for the stop distance (closest to being stopped out is what
    // a reader is scanning for); best first for P&L.
    return key === 'stop' ? left - right || byCoin(a, b) : right - left || byCoin(a, b)
  })
}

const pnlClass = (pnl: number | null) =>
  pnl == null ? 'text-gray-400' : pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-gray-400'

/** Cushion colouring: the stop fires at 0 ATR, so a thin cushion is the warning. */
const cushionClass = (atr: number | null) => {
  if (atr == null) return 'text-gray-400'
  if (atr <= 0.75) return 'text-red-400'
  if (atr <= 1.5) return 'text-amber-400'
  return 'text-gray-200'
}

const Cell = ({
  children,
  className = '',
  testId,
}: {
  children: ReactNode
  className?: string
  testId?: string
}) => (
  <td
    className={`whitespace-nowrap px-3 py-2.5 font-mono text-xs tabular-nums ${className}`}
    data-testid={testId}
  >
    {children}
  </td>
)

/**
 * The paper forward test's full open ledger: every sub-position, with the ATR
 * the stop is measured in and the engine's current trailing-stop level.
 *
 * Read-only by design. The forward test is a frozen, no-discretion experiment
 * (PAPER_STRATEGY_SPEC.md), so there is deliberately no close button here —
 * positions leave only when the engine's own daily run stops them out.
 */
const PaperPositions = () => {
  const [positions, setPositions] = useState<PaperPosition[] | null>(null)
  const [counts, setCounts] = useState<PaperOpenCounts | null>(null)
  const [prices, setPrices] = useState<Record<string, number>>({})
  const [failed, setFailed] = useState(false)
  const [sort, setSort] = useState<SortKey>('stop')

  useEffect(() => {
    let active = true
    let loaded = false

    const load = async () => {
      // A backgrounded tab keeps whatever it last drew rather than polling on.
      if (loaded && document.hidden) return
      try {
        const [rows, nextCounts] = await Promise.all([
          paperAPI.positions({ status: 'open', limit: POSITION_LIMIT }),
          loadPaperOpenCounts(),
        ])
        if (!active) return
        loaded = true
        setPositions(rows)
        setCounts(nextCounts)
        setFailed(false)
      } catch (error) {
        if (!active) return
        console.error('Paper positions refresh failed:', error)
        setFailed(true)
        return
      }

      try {
        // One batched call covers every tracked coin, so the page costs the
        // venue the same whether it shows 5 positions or 200.
        const tape = await marketAPI.getTicker()
        if (!active) return
        setPrices(
          Object.fromEntries(tape.tickers.map((quote: TickerQuote) => [quote.symbol, quote.price]))
        )
      } catch {
        // Display only: rows render with "—" where a price would be.
      }
    }

    load()
    const timer = setInterval(load, POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  const rows = useMemo(
    () => (positions ? sortRows(buildRows(positions, prices), sort) : []),
    [positions, prices, sort]
  )
  // Counts come from the account. The list stops at POSITION_LIMIT, and when
  // it does the page says so rather than passing the page off as the ledger.
  const truncated = counts != null && rows.length >= POSITION_LIMIT && counts.open > rows.length
  const countLine =
    positions == null
      ? '—'
      : [
          counts ? `${counts.open} open` : '— open',
          counts?.coins != null ? plural(counts.coins, 'coin') : null,
          truncated ? `showing ${rows.length} of ${counts.open}` : null,
        ]
          .filter(Boolean)
          .join(' · ')

  return (
    <TerminalCanvas>
      <div className="space-y-6">
        <div className="space-y-2">
          <Link
            to="/?view=paper"
            className="inline-flex items-center gap-1 rounded text-xs text-gray-400 transition-colors hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
          >
            <span aria-hidden="true">&larr;</span> Back to dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-50">Paper open positions</h1>
          <p className="max-w-3xl text-sm text-gray-400">
            Every open sub-position in the paper forward test — one per breakout window holding a coin.
            Pretend money. The trailing stop below is the engine&rsquo;s own level: it ratchets up with the
            highest close since entry and never moves down.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-xs tabular-nums text-gray-500" data-testid="paper-full-counts">
            {countLine}
          </p>
          <div className="flex items-center gap-2" role="group" aria-label="Sort positions">
            <span className="text-xs text-gray-500">Sort by</span>
            {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setSort(key)}
                aria-pressed={sort === key}
                data-testid={`paper-sort-${key}`}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
                  sort === key
                    ? 'border-violet-500/50 bg-violet-500/15 text-violet-200'
                    : 'border-terminal-border text-gray-400 hover:border-violet-500/30 hover:text-gray-200'
                }`}
              >
                {SORT_LABELS[key]}
              </button>
            ))}
          </div>
        </div>

        {positions == null ? (
          <p className="py-16 text-center text-sm text-gray-500">
            {failed ? 'Paper positions are unavailable right now.' : 'Loading positions…'}
          </p>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-terminal-border py-16 text-center">
            <p className="text-sm text-gray-300">No open paper positions</p>
            <p className="mt-1 text-xs text-gray-500">
              The strategy waits for a breakout to a new N-day high while BTC holds its 200-day average.
            </p>
          </div>
        ) : (
          // Only the table scrolls sideways on a phone; the page itself never does.
          <div className="overflow-x-auto rounded-lg border border-terminal-border">
            <table className="min-w-full divide-y divide-terminal-border/70">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                  <th scope="col" className="px-3 py-2 font-medium">Coin</th>
                  <th scope="col" className="px-3 py-2 font-medium">Window</th>
                  <th scope="col" className="px-3 py-2 font-medium">Entry</th>
                  <th scope="col" className="px-3 py-2 font-medium">Current</th>
                  <th
                    scope="col"
                    className="px-3 py-2 font-medium"
                    title="ATR(14) on the signal day — the value stored with the position"
                  >
                    ATR @ entry
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium" title="Highest close since entry, minus 3 ATR">
                    Trailing stop
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">Above stop</th>
                  <th scope="col" className="px-3 py-2 font-medium">P&amp;L</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-terminal-border/40">
                {rows.map((row) => (
                  <tr
                    key={row.position.id}
                    className="transition-colors hover:bg-terminal-raised/60"
                    data-testid="paper-full-row"
                  >
                    <Cell className="font-sans font-semibold text-gray-50">{row.base}</Cell>
                    <Cell>
                      <span className="inline-flex items-center rounded bg-violet-500/10 px-1 py-px text-[10px] font-bold uppercase leading-4 text-violet-300 ring-1 ring-inset ring-violet-500/30">
                        {row.window}D
                      </span>
                    </Cell>
                    <Cell className="text-gray-300">${formatPrice(row.entry)}</Cell>
                    <Cell className="text-gray-100" testId="paper-full-price">
                      {row.price == null ? '—' : `$${formatPrice(row.price)}`}
                    </Cell>
                    <Cell className="text-gray-400" testId="paper-full-atr">
                      ${formatPrice(row.atr)}
                    </Cell>
                    <Cell className="text-gray-300" testId="paper-full-stop">
                      ${formatPrice(row.stopPrice)}
                    </Cell>
                    <Cell className={cushionClass(row.aboveStopAtr)} testId="paper-full-above-stop">
                      {row.aboveStopPct == null ? (
                        '—'
                      ) : (
                        <>
                          {row.aboveStopPct.toFixed(1)}%
                          {row.aboveStopAtr != null && (
                            <span className="ml-1 text-gray-500">({row.aboveStopAtr.toFixed(2)} ATR)</span>
                          )}
                        </>
                      )}
                    </Cell>
                    <Cell className={`font-semibold ${pnlClass(row.pnlPct)}`} testId="paper-full-pnl">
                      {row.pnlPct == null ? '—' : formatPnlPct(row.pnlPct)}
                    </Cell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Display only. The paper test runs without discretion, so positions cannot be closed by hand —
          the daily run exits them when the close breaks the trailing stop.
        </p>
      </div>
    </TerminalCanvas>
  )
}

export default PaperPositions
